import {useHalState} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {CategorieDeTarif} from 'api-types/tolede';
import {ModeTarifaireSouhaite} from 'api-types/ffconnect';
import {usePeutVoirConfigurationPrixTolede} from '@/hooks/auth/usePermissionsCommerciales';

/**
 * Retourne la catégorie de tarif à afficher sur un tag prix (Prix métier, Marché, Dérogation, etc.)
 * uniquement pour les utilisateurs ayant la permission `voir-configuration-prix-tolede`.
 *
 * La source dépend du `contexte`, c'est-à-dire de l'écran où le tag est affiché :
 * - `'panier'` (page panier, side panel) : `configuration-prix-ligne-panier`, qui donne la
 *   catégorie réellement appliquée à la date de livraison du panier, dérogations comprises.
 * - `'catalogue'` (cadencier, listes, produits par saison, OC, UB, cards catalogue) : la catégorie
 *   du catalogue (`prix_par_livraison[0].categorie`), même quand l'article est déjà au panier — la
 *   catégorie de la ligne ne vaut que pour ce panier et n'a pas à s'afficher dans le catalogue.
 *   `autre`, que le serveur pose quand Tolede ne renseigne pas cette catégorie, est transmis
 *   tel quel : `PrixTag` affiche « Non précisé », ou « Marché » si le type de prix le dit.
 *
 * Dans les deux cas, un prix saisi ou bloqué par le commercial (`prix_dero_souhaite`) prime et
 * renvoie `modeTarifaireSouhaite` à la place de la catégorie.
 *
 * Ne gère PAS le tag "Promotion" issu de `typePrix` (NatureDuPrix) : celui-ci est visible par tous
 * les utilisateurs et est passé directement à `PrixTag` via la prop `typePrix`.
 */
export function usePrixCategorieTag(
  ligneDePanier: State<LigneDePanierDesc> | undefined,
  categorieCatalogue: CategorieDeTarif | undefined,
  {contexte}: {contexte: 'panier' | 'catalogue'},
): {
  categorieDeTarif: CategorieDeTarif | undefined;
  modeTarifaireSouhaite: ModeTarifaireSouhaite | undefined;
} {
  const peutVoirConfigurationPrix = usePeutVoirConfigurationPrixTolede();

  const configPrixState = useHalState(
    peutVoirConfigurationPrix && contexte === 'panier' && ligneDePanier
      ? ligneDePanier.follow('configuration-prix-ligne-panier')
      : undefined,
  );

  if (!peutVoirConfigurationPrix) {
    return {categorieDeTarif: undefined, modeTarifaireSouhaite: undefined};
  }

  // Article dans le panier : utiliser la configuration précise (tient compte des dérogations).
  // Fallback sur la catégorie catalogue pendant le fetch de la config pour éviter un flash
  // du tag lors de l'ajout au panier (transition undefined -> categorie_tarif_applique).
  // En contexte catalogue le lien n'est jamais suivi : configPrixState.data reste undefined,
  // et ce `?? categorieCatalogue` devient le cas normal.
  if (ligneDePanier) {
    const prixSaisiActif = ligneDePanier.data.prix_dero_souhaite != null;
    return {
      categorieDeTarif: !prixSaisiActif
        ? (configPrixState.data?.categorie_tarif_applique ?? categorieCatalogue)
        : undefined,
      modeTarifaireSouhaite: prixSaisiActif
        ? (ligneDePanier.data.mode_tarifaire_souhaite ?? undefined)
        : undefined,
    };
  }

  // Article hors panier : utiliser la catégorie du catalogue
  return {
    categorieDeTarif: categorieCatalogue,
    modeTarifaireSouhaite: undefined,
  };
}
